<template>
  <MovableDialog v-if="voiceChat.town?.name" class="voiceChat modal" :position="userPreferences.voiceChat.position"
    :isOpen="userPreferences.voiceChat.opened" :hasNotif="false" :title="voiceChat.town?.name" @toggle="handleToggle">
    <div v-if="userPreferences.discordName" class="button-group">
      <button v-if="!session.isPlayerOrSpectator" :class="['button', { demon: voiceChat.isMuted }]"
        :title="voiceChat.isMuted ? t('modal.voiceChat.unmute') : t('modal.voiceChat.mute')"
        @click="voiceChat.toggleMute()">
        <font-awesome-icon :icon="voiceChat.isMuted ? 'volume-mute' : 'volume-up'" />
      </button>
      <button v-if="grimoire.gamePhase === 'firstNight' || grimoire.gamePhase === 'otherNight'" class="button"
        :title="t('modal.voiceChat.goToSleep')" @click="voiceChat.goToSleep()">
        <font-awesome-icon v-if="session.isPlayerOrSpectator" icon="bed" />
        <font-awesome-icon v-if="!session.isPlayerOrSpectator" icon="user-secret" />
      </button>
      <button
        v-if="(grimoire.gamePhase === 'firstNight' || grimoire.gamePhase === 'otherNight') && !session.isPlayerOrSpectator"
        class="button" :title="t('modal.voiceChat.sendToSleep')" @click="voiceChat.sendToSleep()"><font-awesome-icon
          icon="bed" /></button>
      <button v-if="grimoire.gamePhase !== 'firstNight' && grimoire.gamePhase !== 'otherNight'" class="button"
        :title="t('modal.voiceChat.moveToTownsquare')" @click="voiceChat.moveToChannel('townsquare')"><font-awesome-icon
          icon="landmark-dome" /></button>
      <button v-if="grimoire.gamePhase === 'day' && !session.isPlayerOrSpectator"
        :title="t('modal.voiceChat.gatherTownsquare')" @click="voiceChat.gatherTownsquare()"><font-awesome-icon
          icon="arrows-to-dot" /></button>
      <button v-if="grimoire.gamePhase === 'day'" class="button" :title="t('modal.voiceChat.goDowntown')"
        @click="voiceChat.goDowntown()"><font-awesome-icon icon="arrow-right-to-city" /></button>
    </div>
    <div v-else class="button-group">
      <button class="button" @click="userPreferences.setDiscordName()">{{ t('menu.setDiscordName') }} <font-awesome-icon
          :icon="['fab', 'discord']" /></button>
    </div>
    <ul v-if="voiceChat.town">
      <li v-for="channel in filteredChannels" :key="channel.id">
        {{ channel.name }} <em v-if="channel.members.length">({{ channel.members.length }})</em> <button
          v-if="!channel.members.some((m) => m.tag === userPreferences.discordName)" class="button small"
          @click="voiceChat.moveToChannel(channel.id)"><font-awesome-icon icon="right-to-bracket" /></button>
        <ul>
          <li v-for="member in sortMembers(channel.members)" :key="member.id" class="member"
            :class="[{ self: member.tag === userPreferences.discordName }, member.voiceRole]">
            {{ member.displayName }}
          </li>
        </ul>
      </li>
    </ul>
  </MovableDialog>
</template>

<script setup lang="ts">
import { MovableDialog } from "@/components";
import {
  SLEEP_CHANNEL_ICON,
  TOWNSQUARE_CHANNEL_ICON,
} from "@/consts";
import {
  useGrimoireStore,
  useLocaleStore,
  useSessionStore,
  useUserPreferencesStore,
  useVoiceChatStore
} from "@/stores";
import type {
  DiscordMember,
} from "@/types";
import { computed } from "vue";

const grimoire = useGrimoireStore();
const locale = useLocaleStore();
const session = useSessionStore();
const userPreferences = useUserPreferencesStore();
const voiceChat = useVoiceChatStore();

const t = locale.t;

const handleToggle = () => {
  userPreferences.voiceChat.opened = !userPreferences.voiceChat.opened;
};

const filteredChannels = computed(() => {
  let filteredChannels = voiceChat.town!.channels.filter(channel => channel.name.charAt(0) !== "[");
  if (!session.sessionId || session.isPlayerOrSpectator) {
    filteredChannels = filteredChannels.filter(channel => !channel.name.includes(SLEEP_CHANNEL_ICON)); // TODO: filter upstream instead of on client
  }
  return filteredChannels.sort((c1, c2) => {
    if (c1.name.includes(TOWNSQUARE_CHANNEL_ICON)) return -1;
    if (c2.name.includes(TOWNSQUARE_CHANNEL_ICON)) return 1;
    return c1.name.localeCompare(c2.name);
  })
});

function sortMembers(members: DiscordMember[]) {
  const rank = (voiceRole: DiscordMember["voiceRole"]) =>
    voiceRole === "storyteller" ? 0 : voiceRole === "spectator" ? 2 : 1;
  // Sort a copy to avoid mutating the reactive store state in place from the template, which would cause a render loop.
  return [...members].sort(
    (m1, m2) =>
      rank(m1.voiceRole) - rank(m2.voiceRole) ||
      m1.displayName.localeCompare(m2.displayName),
  )
}
</script>

<style lang="scss" scoped>
@use "@/vars.scss" as *;

.voiceChat {
  max-width: 95vw;
  backdrop-filter: blur(3px);
  width: max-content;
  text-wrap: nowrap;

  ul {
    display: block;
    margin-bottom: .25rem;

    ul {
      font-size: 0.75em;
      margin-left: 1.5rem;
      margin-bottom: 0.25rem;
    }
  }

  .member {
    &.self {
      color: cyan;

      &::before {
        content: '> ';
      }
    }

    &.storyteller {
      color: #c084fc;
    }

    &.spectator {
      color: #868686;
    }
  }

  button.small {
    scale: 0.75;
  }
}
</style>

<style lang="scss">
dialog.voiceChat h3 svg:hover path {
  fill: url(#demon);
  stroke-width: 30px;
  stroke: white;
}
</style>
